import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TENANT_STATUS, adminApi, formatMoney } from '@wa/shared';
import { Cell, DataTable, Empty, Page, Stat } from '@/components/Primitives';

/**
 * What the platform sent, and what it was paid.
 *
 * Three revenue figures, all shown, because they disagree with each other every month
 * and picking one silently is how the gap becomes an argument. The headline is cash
 * received <b>exclusive of GST</b>: the tax collected is money held on the
 * government's behalf, and putting it in the big number overstates the business by
 * eighteen per cent.
 */
export function ReportsPage() {
  const today = new Date();
  const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;

  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState('');

  const overview = useQuery({
    queryKey: ['admin', 'platform-overview', from, to],
    queryFn: () => adminApi.platformOverview(from || undefined, to || undefined),
  });

  const tenants = useQuery({
    queryKey: ['admin', 'platform-tenants', from, to],
    queryFn: () => adminApi.platformTenants(from || undefined, to || undefined),
  });

  const totals = overview.data?.totals;

  return (
    <Page title="Reports" subtitle="Platform-wide volume and revenue.">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-small">
          <span className="mb-1 block text-(--color-muted)">From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                 className="rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-body" />
        </label>
        <label className="text-small">
          <span className="mb-1 block text-(--color-muted)">To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                 className="rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-body" />
        </label>
      </div>

      {totals && (
        <>
          <div className="mb-2 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Cash received" value={`₹${formatMoney(totals.cashReceived)}`} tone="good" />
            <Stat label="Invoiced" value={`₹${formatMoney(totals.invoiced)}`} />
            <Stat label="Credit consumed" value={`₹${formatMoney(totals.creditConsumed)}`} />
            <Stat label="Unused credit held" value={`₹${formatMoney(totals.outstandingCredit)}`} tone="warn" />
          </div>

          <p className="mb-6 max-w-prose text-small text-(--color-muted)">
            <b>Cash received</b> is what was captured in this range, net of GST — the tax is not
            ours to count. <b>Invoiced</b> is what was billed, which diverges the moment a top-up
            goes unpaid. <b>Credit consumed</b> is what clients actually burned on messages: money
            received earlier, not now. <b>Unused credit</b> is a liability — paid for and not yet
            spent.
          </p>

          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Messages sent" value={totals.outbound.toLocaleString('en-IN')} />
            <Stat label="Messages received" value={totals.inbound.toLocaleString('en-IN')} />
            <Stat label="Failed" value={totals.failed.toLocaleString('en-IN')} tone={totals.failed > 0 ? 'warn' : 'ink'} />
            <Stat
              label="Clients"
              value={`${totals.activeTenants} active${totals.suspendedTenants ? `, ${totals.suspendedTenants} suspended` : ''}`}
            />
          </div>
        </>
      )}

      <h2 className="mb-2 text-body font-semibold">By client</h2>

      <DataTable head={['Client', 'Status', 'Sent', 'Credit consumed ₹', 'Cash received ₹', 'Balance ₹', 'Held ₹']}>
        {(tenants.data ?? []).map((row) => (
          <tr key={row.tenantId}>
            <Cell>{row.companyName}</Cell>
            <Cell nowrap>
              <span className={row.status === 1 ? '' : 'text-(--color-warn)'}>
                {TENANT_STATUS[row.status] ?? 'Unknown'}
              </span>
            </Cell>
            <Cell nowrap>{row.messagesSent.toLocaleString('en-IN')}</Cell>
            <Cell nowrap>{formatMoney(row.creditConsumed)}</Cell>
            <Cell nowrap>{formatMoney(row.cashReceived)}</Cell>
            <Cell nowrap>{formatMoney(row.balance)}</Cell>
            <Cell nowrap>{row.held > 0 ? formatMoney(row.held) : '—'}</Cell>
          </tr>
        ))}
      </DataTable>

      {(tenants.data?.length ?? 0) === 0 && !tenants.isLoading && <Empty>No clients yet.</Empty>}
    </Page>
  );
}
